$component-class: 'me-row';
$column-component-class: 'me-column';
$align-items-map: (
  start: flex-start,
  center: center,
  end: flex-end,
  stretch: stretch,
  baseline: baseline,
);
$justify-content-map: (
  start: flex-start,
  center: center,
  end: flex-end,
  between: space-between,
  around: space-around,
  evenly: space-evenly,
);
$flex-wrap-map: (
  wrap: wrap,
  nowrap: nowrap,
  reverse: wrap-reverse,
);
$flex-direction-map: (
  row: row,
  row-reverse: row-reverse,
  column: column,
  column-reverse: column-reverse,
);

@mixin generate-vertical-gap-modifiers($component, $column, $gaps) {
  @each $name, $value in $gaps {
    .#{$component}--y-gap-#{$name} {
      & > .#{$column} {
        margin-bottom: var(--spacing-#{$name});
      }

      // Remove margin from last child to prevent negative margin hack
      & > .#{$column}:last-child {
        margin-bottom: 0;
      }
    }
  }

  @each $name, $value in $gaps {
    .#{$component}--y-gap-md-#{$name} {
      & > .#{$column} {
        @include desktop {
          margin-bottom: var(--spacing-#{$name});
        }
      }

      // Remove margin from last child on desktop
      & > .#{$column}:last-child {
        @include desktop {
          margin-bottom: 0;
        }
      }
    }
  }

  @each $name, $value in $gaps {
    .#{$component}--y-gap-lg-#{$name} {
      & > .#{$column} {
        @include desktop-lg {
          margin-bottom: var(--spacing-#{$name});
        }
      }

      // Remove margin from last child on desktop-lg
      & > .#{$column}:last-child {
        @include desktop-lg {
          margin-bottom: 0;
        }
      }
    }
  }
}

@mixin generate-horizontal-gap-modifiers($component, $column, $gaps) {
  .#{$component} {
    @include grid-row;
  }

  @each $name, $value in $gaps {
    .#{$component}--x-gap-#{$name} {
      @include grid-row-gap(var(--spacing-#{$name}));

      & > .#{$column} {
        @include grid-column-padding(var(--spacing-#{$name}));
      }
    }
  }

  @each $name, $value in $gaps {
    .#{$component}--x-gap-md-#{$name} {
      @include desktop {
        @include grid-row-gap(var(--spacing-#{$name}));
      }

      & > .#{$column} {
        @include desktop {
          @include grid-column-padding(var(--spacing-#{$name}));
        }
      }
    }
  }

  @each $name, $value in $gaps {
    .#{$component}--x-gap-lg-#{$name} {
      @include desktop-lg {
        @include grid-row-gap(var(--spacing-#{$name}));
      }

      & > .#{$column} {
        @include desktop-lg {
          @include grid-column-padding(var(--spacing-#{$name}));
        }
      }
    }
  }
}

@mixin generate-align-items-modifiers($component, $map) {
  @each $key, $value in $map {
    .#{$component}--align-#{$key} {
      align-items: #{$value};
    }
  }

  @each $key, $value in $map {
    .#{$component}--align-md-#{$key} {
      @include desktop {
        align-items: #{$value};
      }
    }
  }

  @each $key, $value in $map {
    .#{$component}--align-lg-#{$key} {
      @include desktop-lg {
        align-items: #{$value};
      }
    }
  }
}

@mixin generate-justify-content-modifiers($component, $map) {
  @each $key, $value in $map {
    .#{$component}--justify-#{$key} {
      justify-content: #{$value};
    }
  }

  @each $key, $value in $map {
    .#{$component}--justify-md-#{$key} {
      @include desktop {
        justify-content: #{$value};
      }
    }
  }

  @each $key, $value in $map {
    .#{$component}--justify-lg-#{$key} {
      @include desktop-lg {
        justify-content: #{$value};
      }
    }
  }
}

@mixin generate-flex-wrap-modifiers($component, $map) {
  @each $key, $value in $map {
    .#{$component}--wrap-#{$key} {
      flex-wrap: #{$value};
    }
  }

  @each $key, $value in $map {
    .#{$component}--wrap-md-#{$key} {
      @include desktop {
        flex-wrap: #{$value};
      }
    }
  }

  @each $key, $value in $map {
    .#{$component}--wrap-lg-#{$key} {
      @include desktop-lg {
        flex-wrap: #{$value};
      }
    }
  }
}

@mixin generate-flex-direction-modifiers($component, $map) {
  @each $key, $value in $map {
    .#{$component}--direction-#{$key} {
      flex-direction: #{$value};
    }
  }

  @each $key, $value in $map {
    .#{$component}--direction-md-#{$key} {
      @include desktop {
        flex-direction: #{$value};
      }
    }
  }

  @each $key, $value in $map {
    .#{$component}--direction-lg-#{$key} {
      @include desktop-lg {
        flex-direction: #{$value};
      }
    }
  }
}

@include generate-horizontal-gap-modifiers($component-class, $column-component-class, $spacings);
@include generate-vertical-gap-modifiers($component-class, $column-component-class, $spacings);
@include generate-align-items-modifiers($component-class, $align-items-map);
@include generate-justify-content-modifiers($component-class, $justify-content-map);
@include generate-flex-wrap-modifiers($component-class, $flex-wrap-map);
@include generate-flex-direction-modifiers($component-class, $flex-direction-map);

// Handle xGap="none" - remove horizontal gap
.#{$component-class}--x-gap-none {
  margin-left: 0;
  margin-right: 0;

  & > .#{$column-component-class} {
    padding-left: 0;
    padding-right: 0;
  }
}

// Handle xGapMd="none" - remove horizontal gap on desktop
.#{$component-class}--x-gap-md-none {
  @include desktop {
    margin-left: 0;
    margin-right: 0;

    & > .#{$column-component-class} {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

// Handle xGapLg="none" - remove horizontal gap on desktop-lg
.#{$component-class}--x-gap-lg-none {
  @include desktop-lg {
    margin-left: 0;
    margin-right: 0;

    & > .#{$column-component-class} {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

// Handle yGap="none" - remove vertical gap
.#{$component-class}--y-gap-none {
  & > .#{$column-component-class} {
    margin-bottom: 0;
  }
}

// Handle yGapMd="none" - remove vertical gap on desktop
.#{$component-class}--y-gap-md-none {
  & > .#{$column-component-class} {
    @include desktop {
      margin-bottom: 0;
    }
  }
}

// Handle yGapLg="none" - remove vertical gap on desktop-lg
.#{$component-class}--y-gap-lg-none {
  & > .#{$column-component-class} {
    @include desktop-lg {
      margin-bottom: 0;
    }
  }
}

// Remove margin-bottom from last column when flex-direction is column
// Only apply on mobile (below desktop)
.#{$component-class}--direction-column,
.#{$component-class}--direction-column-reverse {
  @include below-desktop {
    & > .#{$column-component-class}:last-child {
      margin-bottom: 0;
    }
  }
}

// Also apply on desktop if direction is column and NO directionMd/directionLg-row override
.#{$component-class}--direction-column:not(.#{$component-class}--direction-md-row):not(.#{$component-class}--direction-md-row-reverse):not(
    .#{$component-class}--direction-lg-row
  ):not(.#{$component-class}--direction-lg-row-reverse),
.#{$component-class}--direction-column-reverse:not(.#{$component-class}--direction-md-row):not(.#{$component-class}--direction-md-row-reverse):not(
    .#{$component-class}--direction-lg-row
  ):not(.#{$component-class}--direction-lg-row-reverse) {
  @include desktop {
    & > .#{$column-component-class}:last-child {
      margin-bottom: 0;
    }
  }
}

// On desktop with directionMd column, remove margin from last child
.#{$component-class}--direction-md-column,
.#{$component-class}--direction-md-column-reverse {
  @include desktop {
    & > .#{$column-component-class}:last-child {
      margin-bottom: 0;
    }
  }
}

// On desktop-lg with directionLg column, remove margin from last child
.#{$component-class}--direction-lg-column,
.#{$component-class}--direction-lg-column-reverse {
  @include desktop-lg {
    & > .#{$column-component-class}:last-child {
      margin-bottom: 0;
    }
  }
}
